import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose?: () => void;
  errorDetails?: string[];
}

interface ToastContainerProps {
  type: string;
  visible: boolean;
  expanded?: boolean;
  hasDetails?: boolean;
}

const ToastContainer = styled.div<ToastContainerProps>`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 12px 20px;
  width: 350px;
  max-width: 90vw;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  font-size: 14px;
  z-index: 1200;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: translateY(${(props) => (props.visible ? '0' : '-20px')});
  transition: opacity 0.3s, transform 0.3s;
  cursor: ${(props) => (props.hasDetails ? 'pointer' : 'default')};
  user-select: none;
  
  /* Different background colors based on type */
  background-color: ${(props) => {
    switch (props.type) {
      case 'success':
        return '#4CAF50';
      case 'error':
        return '#F44336';
      case 'warning':
        return '#FF9800';
      case 'info':
      default:
        return '#2196F3';
    }
  }};
  color: white;
`;

const ToastHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const MessageText = styled.span`
  flex: 1;
  word-break: break-word;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  margin-left: 10px;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 16px;
  opacity: 0.6;
  
  &:hover {
    opacity: 1;
  }
`;

const DetailsList = styled.div`
  margin-top: 10px;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  max-height: 180px;
  overflow-y: auto;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.25);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.4);
    }
  }
`;

const DetailItem = styled.div`
  padding: 2px 4px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
  word-break: break-word;
  font-family: inherit;
  font-size: 12px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  
  &::before {
    content: "•";
    color: rgba(255, 255, 255, 0.7);
    font-weight: bold;
  }
`;

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 5000,
  onClose,
  errorDetails = [],
}) => {
  const [visible, setVisible] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // If there are error details, do not auto-dismiss
    if (errorDetails && errorDetails.length > 0) {
      return;
    }

    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => {
        if (onClose) onClose();
      }, 300); // Wait for fade out animation before calling onClose
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose, errorDetails]);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent toggling details
    setVisible(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  const hasDetails = errorDetails && errorDetails.length > 0;

  const handleContainerClick = () => {
    if (hasDetails) {
      setShowDetails(!showDetails);
    }
  };

  return (
    <ToastContainer 
      type={type} 
      visible={visible} 
      expanded={showDetails}
      hasDetails={hasDetails}
      onClick={handleContainerClick}
    >
      <ToastHeader>
        <MessageText>{message}</MessageText>
        <CloseButton onClick={handleClose}>×</CloseButton>
      </ToastHeader>
      
      {hasDetails && showDetails && (
        <DetailsList onClick={(e) => e.stopPropagation()}>
          {errorDetails.map((detail, index) => (
            <DetailItem key={index}>{detail}</DetailItem>
          ))}
        </DetailsList>
      )}
    </ToastContainer>
  );
};

export default Toast; 